import {
  Card,
  CardContent,
  CardMedia,
  Grid,
  Paper,
  styled,
  Theme,
  Tooltip,
} from "@mui/material";
import { paletteVar, Typography } from "@platform/design-system";
import { JSX, useMemo } from "react";

import { Default, Dental } from "../../../assets/coverages";

const SURFACE_NEUTRAL = paletteVar("surfaces-neutral", "#f2f2f2");

const CoveragesGrid = styled(Grid)(({ theme }: { theme: Theme }) => ({
  rowGap: theme.spacing(3),
  columnGap: theme.spacing(2),
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(10rem, 1fr))",
  gridAutoRows: "1fr",
  cursor: "pointer",
  width: "100%",
}));

const CoveragesItemContainer = styled(Card)(({ theme }: { theme: Theme }) => ({
  display: "flex",
  flexDirection: "column",
  "&.disabled": {
    pointerEvents: "none",
    border: `1px solid ${SURFACE_NEUTRAL}`,
    ".MuiCardMedia-root": {
      filter: "grayscale(100%) brightness(85%) contrast(30%) opacity(38%)",
    },
    ".MuiCardContent-root": {
      backgroundColor: SURFACE_NEUTRAL,
      p: { color: theme.palette.text.disabled },
    },
  },
  "&.selected": {
    boxShadow: `0 0 0 2px ${theme.palette.brandSupporting.light}`,
    ".MuiCardContent-root": {
      backgroundColor: paletteVar("surfaces-primary", "#EFF8FB"),
    },
  },
  ".MuiCardContent-root": {
    backgroundColor: SURFACE_NEUTRAL,
  },
}));

const CoveragesItemMedia = styled(CardMedia)(({ theme }: { theme: Theme }) => ({
  minHeight: theme.spacing(17),
  backgroundSize: "auto",
  flex: 2,
}));

const CoveragesItemContent = styled(CardContent)(
  ({ theme }: { theme: Theme }) => ({
    padding: theme.spacing(1, 4),
    display: "flex",
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    minHeight: "4rem",
    "&:last-child": {
      paddingBottom: theme.spacing(1),
    },
    p: {
      textAlign: "center",
      color: paletteVar("text-primary", "#00344F"),
    },
  }),
);

type DataCoverage = {
  id: number;
  name: string;
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
};

type CoverageType = "products" | "clientProducts";

type CoverageGridProps = {
  title: string;
  all: Record<CoverageType, DataCoverage[]>;
  allSelected: Record<CoverageType, DataCoverage[]>;
  onSelect: (coverage: DataCoverage, type: CoverageType) => void;
  type: CoverageType;
};

export const CoverageGrid = ({
  title,
  all,
  allSelected,
  onSelect,
  type,
}: CoverageGridProps): JSX.Element => {
  const data = all[type];
  const coverageList = useMemo(() => {
    if (data.length === 0) return [];

    const dentalList = data.filter((value) =>
      value.name?.toLowerCase().includes("dental"),
    );
    const otherList = data.filter(
      (value) => !value.name?.toLowerCase().includes("dental"),
    );
    return [...dentalList, ...otherList];
  }, [data]);

  if (coverageList.length === 0) return null;

  const selected = allSelected[type];

  return (
    <Grid container component={Paper} mb={3} padding={5}>
      <Grid size={{ xs: 12 }} mb={3}>
        <Typography variant="header-large">{title}</Typography>
      </Grid>
      <CoveragesGrid container>
        {coverageList.map((coverage) => {
          const isSelected = Boolean(
            selected?.some((v) => v.name === coverage.name),
          );

          const clearImageName = coverage.name.replace(/[\s|&^/]/g, "");
          let image: unknown;

          try {
            image = new URL(
              `../../../assets/coverages/${clearImageName}.svg`,
              import.meta.url,
            ).href;
            if (typeof image === "string" && image.endsWith("undefined")) {
              image = clearImageName?.toLowerCase().includes("dental")
                ? Dental
                : Default;
            }
          } catch {
            image = Default;
          }

          return (
            <CoveragesItemContainer
              key={coverage.id}
              className={isSelected ? "selected" : ""}
              onClick={() => onSelect(coverage, type)}
            >
              <CoveragesItemMedia image={image} />
              <CoveragesItemContent>
                <Tooltip title={coverage.name}>
                  <Typography>{coverage.name}</Typography>
                </Tooltip>
              </CoveragesItemContent>
            </CoveragesItemContainer>
          );
        })}
      </CoveragesGrid>
    </Grid>
  );
};

export { CoverageType };
